<template>
  <FullscreenModal :open="visible" bg-shadow @close="dismiss">
    <div
      data-testid="license-expiry-modal"
      class="pointer-events-auto flex w-[560px] max-w-[90vw] flex-col items-center border border-gray-secondary bg-black-primary p-8 text-center"
    >
      <div
        class="mb-2 flex items-center gap-2 text-[28px] font-semibold leading-tight text-red-additional"
      >
        <AlertIcon class="h-7 w-7" />
        <span>{{ t("license.modal.attention") }}</span>
      </div>
      <p class="mb-3 text-[24px] font-medium leading-snug text-white-primary">
        {{ t("license.modal.expiring_subtitle") }}
      </p>
      <p class="mb-8 text-[16px] leading-[150%] text-gray-primary">
        {{ t("license.modal.description") }}
      </p>
      <button
        type="button"
        data-testid="license-expiry-modal-close"
        class="w-2/3 rounded-md bg-gray-secondary px-4 py-2.5 text-button-primary text-white-primary transition-opacity hover:opacity-90"
        @click="dismiss"
      >
        {{ t("license.modal.close") }}
      </button>
    </div>
  </FullscreenModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Locales, MessageSchema } from "@/i18n";
import { storeToRefs } from "pinia";
import FullscreenModal from "@/components/ui/overlays/FullscreenModal.vue";
import AlertIcon from "@/icons/alert.svg";
import { useLicenseStore } from "@/stores/settings/license";
import { useAuthStore } from "@/stores/auth/session";

const { t } = useI18n<{ message: MessageSchema }, Locales>();

const licenseStore = useLicenseStore();
const { info, isCritical } = storeToRefs(licenseStore);
const authStore = useAuthStore();
const { token } = storeToRefs(authStore);

// Keyed by user *and* expiry date: a renewal surfaces the modal again, and one
// user's dismissal doesn't hide it from another on the same browser.
const DISMISS_KEY_PREFIX = "license_modal_dismissed_until";
const dismissKey = computed(() =>
  token.value?.id != null ? `${DISMISS_KEY_PREFIX}:${token.value.id}` : null,
);

const dismissedFor = ref<string | null>(null);
watch(
  dismissKey,
  (key) => {
    dismissedFor.value = key ? localStorage.getItem(key) : null;
  },
  { immediate: true },
);

const visible = computed(() => {
  if (!info.value || !isCritical.value) return false;
  return dismissedFor.value !== info.value.until;
});

// Closing must not depend on having somewhere to persist it: with no user id
// the dismissal is session-local, but the modal still closes.
function dismiss() {
  if (!info.value) return;
  dismissedFor.value = info.value.until;
  if (dismissKey.value)
    localStorage.setItem(dismissKey.value, info.value.until);
}
</script>
